import { ComponentProps, ReactElement, useLayoutEffect } from 'react';
import { View, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Text } from '@ui-kitten/components';
import { useNavigation } from '@react-navigation/native';
import { CreateResponsiveStyle, DEVICE_SIZES, minSize } from 'rn-responsive-styles';

import { HeaderBackButton, Divider } from '@pletly/ui';
import { getName } from '@pletly/shared';
import { Invite } from '%components/Invite';
import { ListDefaultLayout } from '%components';

import { Friend } from '%components/FriendsList/Friend';
import { colors, spacing } from '%theme';

import { Loader } from '%components/Profile/CareReceiverConnections';
import { AddConnectionMenu } from '%components/Posts/ConnectionsAndInfoCard/AddConnectionMenu';
import { CONNECTIONS_MANAGE_ENABLED } from '%constants/connections';
import { useFriendsAndIvnites } from './useFriendsAndInvites';
import { PlaceholderType, DataType } from './types';
import { EmptyList } from './EmptyList';

export const FriendsAndInvites = ({ style, contentContainerStyle }: Props) => {
	const { t } = useTranslation();
	const { setOptions, navigate, goBack } = useNavigation();
	const styles = useStyles();

	const {
		acceptInvitation,
		handleDeleteFriend,
		handleReject,
		friendsLoading,
		invitesLoading,
		refetchFriends,
		refreshInvites,
		listItems,
		handleAcceptImportRequest,
		handleRejectImportRequest,
	} = useFriendsAndIvnites();

	useLayoutEffect(() => {
		setOptions({
			headerShown: true,
			headerTitle: t('connections.sent-invites'),
			headerRight: null,
			headerLeft: () => <HeaderBackButton onPress={goBack} />,
		});
	}, [navigate, setOptions, t, goBack]);

	if (friendsLoading || invitesLoading) {
		return <Loader itemsCount={10} style={styles.loader} />;
	}

	if (!listItems) {
		return null;
	}

	return (
		<ListDefaultLayout
			style={style}
			contentContainerStyle={contentContainerStyle}
			data={listItems}
			ListEmptyComponent={EmptyList}
			renderItem={({ item }) => {
				if (item.type === PlaceholderType.Divider) {
					return <Divider style={styles.divider} />;
				}

				if (item.type === PlaceholderType.Title) {
					if (Platform.OS === 'web') {
						return (
							<WebTitle
								title={item.title}
								Action={
									item.title === t('connections.my-connections') ? <AddConnectionMenu /> : undefined
								}
							/>
						);
					}

					return <Title title={item.title} />;
				}

				if (item.type === DataType.Friends) {
					const {
						data: { user, endDate },
					} = item;
					const onDelete = CONNECTIONS_MANAGE_ENABLED
						? () => handleDeleteFriend(user.uuid, user.username)
						: undefined;

					return (
						<View>
							<Friend
								id={user.id}
								photoUrl={user.photoUrl}
								username={getName(user)}
								onDelete={onDelete}
								size={Platform.select({ default: undefined, web: 48 })}
								bolded={Platform.select({ default: true, web: false })}
							/>
							{endDate ? (
								<Text
									style={styles.temporaryAccessText}
									category={Platform.select({ web: 'c1', default: 'p1' })}
									appearance="hint"
								>
									{t('connections.temporary-access')}
								</Text>
							) : null}
						</View>
					);
				}

				if (
					item.type === DataType.CareReceiverInvites ||
					item.type === DataType.CareReceiverImportToOrganisationInvites
				) {
					const { data } = item;

					return (
						<View>
							{data.toUser?.username && (
								<Text appearance="hint" style={styles.headerPadding}>
									{getName(data.toUser)}
								</Text>
							)}
							<Invite
								user={data.fromUser}
								onAccept={() =>
									item.type === DataType.CareReceiverImportToOrganisationInvites
										? handleAcceptImportRequest(data.id)
										: acceptInvitation(data)
								}
								onDecline={() =>
									item.type === DataType.CareReceiverImportToOrganisationInvites
										? handleRejectImportRequest(data.id)
										: handleReject(data.id)
								}
							/>
						</View>
					);
				}

				const { data } = item;

				return (
					<Invite
						user={data.fromUser}
						onAccept={() => acceptInvitation(data)}
						onDecline={() => handleReject(data.id)}
					/>
				);
			}}
			onRefresh={() => Promise.all([refetchFriends(), refreshInvites()])}
			estimatedItemSize={72}
		/>
	);
};

const Title = ({ title }: { title: string }) => {
	const styles = useStyles();

	return (
		<Text
			style={[styles.text, styles.headerPadding]}
			category={Platform.select({ default: 'h5', web: 'h3' })}
		>
			{title}
		</Text>
	);
};

const WebTitle = ({ title, Action }: { title: string; Action?: ReactElement }) => {
	const styles = useStyles();

	return (
		<View style={styles.webTitle}>
			<Title title={title} />
			{Action}
		</View>
	);
};

const useStyles = CreateResponsiveStyle(
	{
		webTitle: {
			flexDirection: 'column',
			alignItems: 'flex-start',
		},
		text: {
			paddingVertical: spacing(),
			backgroundColor: Platform.select({ default: colors.background, web: 'transparent' }),
		},
		loader: {
			marginTop: -spacing(9),
		},
		headerPadding: {
			paddingHorizontal: spacing(2),
		},
		divider: Platform.select({
			web: {
				marginVertical: 0,
			},
			default: {},
		}),
		temporaryAccessText: {
			marginLeft: spacing(Platform.select({ web: 9, default: 8 })),
			marginTop: -spacing(3.5),
		},
	},
	{
		[minSize(DEVICE_SIZES.MD)]: {
			webTitle: {
				flexDirection: 'row',
				justifyContent: 'space-between',
				alignItems: 'center',
			},
		},
	},
);

type Props = Pick<ComponentProps<typeof ListDefaultLayout>, 'style' | 'contentContainerStyle'>;
